'use client';

import { useState } from 'react';

export default function BmiCalculator() {
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [bmi, setBmi] = useState<number | null>(null);
  const [category, setCategory] = useState('');

  const calculateBMI = () => {
    if (!weight || !height) return;

    const weightInKg = parseFloat(weight);
    const heightInM = parseFloat(height) / 100; // Convert cm to meters

    if (weightInKg <= 0 || heightInM <= 0) return;

    const bmiValue = weightInKg / (heightInM * heightInM);
    setBmi(parseFloat(bmiValue.toFixed(1)));

    // Determine BMI category
    if (bmiValue < 18.5) {
      setCategory('Underweight');
    } else if (bmiValue >= 18.5 && bmiValue < 25) {
      setCategory('Normal weight');
    } else if (bmiValue >= 25 && bmiValue < 30) {
      setCategory('Overweight');
    } else {
      setCategory('Obese');
    }
  };

  const resetCalculator = () => {
    setWeight('');
    setHeight('');
    setBmi(null);
    setCategory('');
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Underweight': return 'text-blue-600';
      case 'Normal weight': return 'text-green-600';
      case 'Overweight': return 'text-yellow-600';
      case 'Obese': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          BMI Calculator
        </h2>

        <div className="space-y-6">
          <div>
            <label htmlFor="weight" className="block text-sm font-medium text-gray-700 mb-2">
              Weight (kg)
            </label>
            <input
              type="number"
              id="weight"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Enter your weight in kg"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              min="1"
              step="0.1"
            />
          </div>

          <div>
            <label htmlFor="height" className="block text-sm font-medium text-gray-700 mb-2">
              Height (cm)
            </label>
            <input
              type="number"
              id="height"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="Enter your height in cm"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              min="1"
              step="0.1"
            />
          </div>

          <div className="flex gap-4">
            <button
              onClick={calculateBMI}
              disabled={!weight || !height}
              className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Calculate BMI
            </button>

            <button
              onClick={resetCalculator}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
          </div>

          {bmi && (
            <div className="mt-6 p-6 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-lg text-gray-600 mb-2">Your BMI is:</p>
                <p className="text-3xl font-bold text-gray-900 mb-2">{bmi}</p>
                <p className={`text-lg font-medium ${getCategoryColor(category)}`}>
                  {category}
                </p>
              </div>

              <div className="mt-4 text-sm text-gray-600">
                <p><strong>BMI Categories:</strong></p>
                <ul className="mt-2 space-y-1">
                  <li>• Underweight: BMI less than 18.5</li>
                  <li>• Normal weight: BMI 18.5–24.9</li>
                  <li>• Overweight: BMI 25–29.9</li>
                  <li>• Obese: BMI 30 or greater</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}